import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { WorkflowConfig } from 'sprites-types/workflow/core'

/**
 * Standard interface for all workflow tool result components.
 * Provides consistency across all tool displays and enables features like markdown export.
 */
export interface WorkflowToolComponentProps {
  nodeExecution: NodeExecution
  workflowConfig?: WorkflowConfig
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
}

/**
 * Standard interface for workflow tool component implementations.
 * Each tool component should implement this interface to provide:
 * 1. Visual display component
 * 2. Markdown export functionality for results download
 */
export interface WorkflowToolComponent {
  /**
   * The React component that renders the tool results
   */
  Component: React.ComponentType<
    WorkflowToolComponentProps & { onOpenPanel?: (content: any, breadcrumbs: any[]) => void }
  >

  /**
   * The function that downloads the tool results. Only applies to tools that have downloadable results. Text is not provided.
   */
  download: (nodeExecution: NodeExecution) => Promise<void>

  /**
   * Converts the tool execution results to markdown format.
   * Used for full results download and sharing functionality.
   *
   * @param nodeExecution - The node execution data
   * @param workflowConfig - The workflow configuration for context
   * @returns Markdown string representation of the results
   */
  toMarkdown: (nodeExecution: NodeExecution, workflowConfig?: WorkflowConfig) => string
}

/**
 * Helper function to create a workflow tool component with both display and markdown export.
 */
export function createWorkflowToolComponent(
  Component: React.ComponentType<
    WorkflowToolComponentProps & { onOpenPanel?: (content: any, breadcrumbs: any[]) => void }
  >,
  download: (nodeExecution: NodeExecution) => Promise<void>,
  toMarkdown: (nodeExecution: NodeExecution, workflowConfig?: WorkflowConfig) => string
): WorkflowToolComponent {
  return {
    Component,
    download,
    toMarkdown,
  }
}
